import React, { useMemo } from "react";
import { User, SharedWorkoutRoutine } from "../../api/types.ts";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { Dropdown } from "../widgets/Dropdown.tsx";
import { DropdownItem } from "../widgets/DropdownItem.tsx";
import { SharedRoutineExerciseList } from "./SharedRoutineExerciseList.tsx";
import {
  useDeleteSharedWorkoutRoutine,
  useImportWorkoutRoutine,
  usePatchSharedWorkoutRoutine,
} from "../../api/hooks.ts";
import { Spinner } from "../widgets/Spinner.tsx";
import { AnimatedLoading } from "../widgets/AnimatedLoading.tsx";
import { Card } from "../containers/Card.tsx";
import { FetchedExercise } from "../../util/exercise.tsx";
import { useDialog } from "../DialogManager.tsx";
import { useToast } from "../ToastContext.tsx";

interface SharedRoutineCardProps {
  routine?: SharedWorkoutRoutine;
  availableExercises: Map<string, FetchedExercise>;
  friends: Partial<User>[] | undefined;
  untilRoutinesLimit: number;
  untilExercisesLimit: number;
  goToRoutines: () => void;
}

const SharedRoutineCard: React.FC<SharedRoutineCardProps> = ({
  routine,
  availableExercises,
  friends,
  untilRoutinesLimit,
  untilExercisesLimit,
  goToRoutines,
}) => {
  const { openDialog, setError } = useDialog();
  const { showInfo } = useToast();

  const isEditable = !!routine?.exercises.every((ex) =>
    availableExercises.get(ex.exercise_id),
  );

  const importWorkoutRoutineMutation = useImportWorkoutRoutine();
  const patchSharedWorkoutRoutineMutation = usePatchSharedWorkoutRoutine();
  const deleteSharedWorkoutRoutineMutation = useDeleteSharedWorkoutRoutine();

  const handleImportRoutine = (routine: SharedWorkoutRoutine) => {
    importWorkoutRoutineMutation.mutate(routine.id, {
      onSuccess: () => {
        showInfo(`Imported routine "${routine.name}"`);
        goToRoutines();
      },
      onError: (e) => {
        setError(e);
      },
    });
  };

  const handleRemoveRoutine = (routine: SharedWorkoutRoutine) => {
    deleteSharedWorkoutRoutineMutation.mutate(routine.id, {
      onError: (e) => {
        setError(e);
      },
    });
  };

  const externalExercises = useMemo(() => {
    var nbr = 0;
    availableExercises.forEach((value) => {
      if (value.external) {
        nbr += 1;
      }
    });

    return nbr;
  }, [availableExercises]);

  const friend = friends?.find((friend) => friend.id === routine?.sender_id);

  const handleDeleteClick = (routine: SharedWorkoutRoutine) => {
    openDialog("confirm", {
      onConfirm: () => handleRemoveRoutine(routine),
      title: "Remove Routine",
      message: `Are you sure you want to remove "${routine.name}" from your shared routines? This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });
  };

  const handleImportClick = (routine: SharedWorkoutRoutine) => {
    openDialog("confirm", {
      onConfirm: () => handleImportRoutine(routine),
      title: "Import Routine",
      message:
        untilExercisesLimit < externalExercises
          ? "Please resolve the issue before proceeding."
          : `Are you sure you want to import "${routine.name}" from ${friend?.name ?? "Unknown Friend"} to your personal routines?`,
      warningMessage:
        externalExercises > 0
          ? `This routine contains ${externalExercises} exercise${externalExercises > 1 ? "s" : ""} that ${externalExercises > 1 ? "don't" : "doesn't"} exist in your personal library. Continuing will import ${externalExercises > 1 ? "these" : "this"} exercise${externalExercises > 1 ? "s" : ""} into your personal library, along with the shared workout routine. If you don't wish to do that, you can close this dialog and edit the exercises of this routine before importing. You also have the option to merge exercises after the import is complete.`
          : undefined,
      dangerMessage:
        untilExercisesLimit < externalExercises
          ? `This routine contains ${externalExercises} exercise${externalExercises > 1 ? "s" : ""} that ${externalExercises > 1 ? "don't" : "doesn't"} exist in your personal library, which exceeds the number you are allowed to create. To proceed, you must close this dialog and either edit the exercises of this routine, or remove or merge exercises in your personal library.`
          : undefined,
      confirmText: "Import",
      variant: "info",
    });
  };

  const handleRoutineExerciseChange = (
    routineExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId: string,
  ) => {
    if (!routine) {
      return;
    }

    const updatedRoutineExercises = routine.exercises.map((ex, idx) => {
      if (idx === routineExerciseIndex) {
        return {
          ...ex,
          exercise_id: newExerciseId,
          resistance_unit: newUnit,
        };
      }
      return ex;
    });

    patchSharedWorkoutRoutineMutation.mutate({
      id: routine.id,
      routine: { exercises: updatedRoutineExercises },
    });
  };

  return (
    <Card>
      <Card.Header>
        <div className="w-full flex justify-between items-center">
          <div>
            <h4 className="font-semibold text-lg m-1 p-1">
              {routine?.name ?? <Spinner />}
            </h4>
            <span className="italic text-gray-600 text-xs flex flex-row mx-2">
              From {friend?.name ?? <Spinner className="mx-1" />} (
              {friend?.email ?? <Spinner className="mx-1" />})
            </span>
          </div>
          <div>
            <Dropdown
              trigger={
                <div className={`flex justify-center`}>
                  <button
                    className="p-1 hover:bg-gray-100 rounded-full  transition-colors"
                    aria-label="Routine options"
                  >
                    <HiOutlineDotsVertical
                      className="text-gray-600 animate-none"
                      size={20}
                    />
                  </button>
                </div>
              }
              alignment="left"
            >
              <DropdownItem
                onClick={() => handleImportClick(routine!)}
                disabled={!routine || untilRoutinesLimit < 1}
              >
                Import
              </DropdownItem>
              <DropdownItem
                onClick={() => handleDeleteClick(routine!)}
                className="text-red-600 hover:bg-red-50"
                disabled={!routine}
              >
                Remove
              </DropdownItem>
            </Dropdown>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        {routine ? (
          <SharedRoutineExerciseList
            resourceId={routine.id}
            routineExercises={routine.exercises}
            availableExercises={availableExercises}
            onExerciseChange={handleRoutineExerciseChange}
            isEditable={isEditable}
          />
        ) : (
          <AnimatedLoading />
        )}
      </Card.Content>
      <Card.Footer>
        {routine && (
          <span className="text-sm text-gray-600">
            <span className="font-medium">{routine.exercises.length}</span>{" "}
            exercise
            {routine.exercises.length !== 1 ? "s" : ""}
          </span>
        )}
      </Card.Footer>
    </Card>
  );
};

export { SharedRoutineCard };
